export interface LscApiErrorBody {
  success?: boolean;
  error?: string;
  message?: string;
  archive_id?: string;
  details?: LscCsvImportDetails;
  errors?: LscCsvImportIssue[] | string[];
  warnings?: LscCsvImportIssue[] | string[];
  detected_format?: string;
}

export interface LscCsvImportIssue {
  code?: string;
  message: string;
  severity?: string;
  row_number?: number;
  field?: string;
  details?: Record<string, unknown>;
}

export interface LscCsvImportDetails {
  missing_columns?: string[];
  detected_columns?: string[];
  detected_format?: string;
  supported_formats?: string[];
  supported_aliases?: Record<string, string>;
}

export interface LscArchiveAutomationPolicy {
  mode?: string;
  auto_run_vod_truth?: boolean;
  auto_approve_clean_handoffs?: boolean;
  auto_publish_clean_handoffs?: boolean;
  publish_delay_minutes?: number;
  default_visibility?: string;
  require_review_for_empty_hud?: boolean;
  require_review_for_restart_continuation?: boolean;
  require_review_for_side_conflict?: boolean;
  require_review_for_low_confidence?: boolean;
  require_review_for_manual_assignment?: boolean;
  minimum_live_truth_confidence?: number;
  minimum_vod_truth_status?: string;
  minimum_auto_archive_score?: number;
  pilot_enabled?: boolean;
  autopublish_mode?: 'off' | 'dry_run' | 'controlled_apply' | string;
  dry_run_first_required?: boolean;
  controlled_apply_locked_until_dry_run_reviewed?: boolean;
  policy_version?: number;
  updated_by?: string | null;
  updated_at?: string | null;
  policy_change_reason?: string | null;
}

/** AP-1 customer-safe archive autopublish settings read model. */
export interface LscArchiveAutopublishSettings {
  autopublish_enabled: boolean;
  autopublish_mode: 'off' | 'dry_run' | 'controlled_apply' | string;
  minimum_confidence_threshold?: number;
  require_verified_live_handoff?: boolean;
  require_confirmed_broadcaster_side?: boolean;
  require_no_needs_attention?: boolean;
  require_no_schedule_stale_or_rollover_blocker?: boolean;
  require_no_team_or_opponent_conflict?: boolean;
  manual_review_fallback_enabled?: boolean;
  manual_hold_blocks_autopublish?: boolean;
  allowed_sources?: string[];
  dry_run_first_required?: boolean;
  controlled_apply_locked_until_dry_run_reviewed?: boolean;
  controlled_apply_actionable?: boolean;
  settings_version?: number;
  last_updated_by_uid?: string | null;
  last_updated_at?: string | null;
  change_reason?: string | null;
  customer_message?: string;
  publish_behavior_enabled?: boolean;
}

/** AP-2 customer-safe archive autopublish eligibility read model. */
export interface LscArchiveAutopublishEligibility {
  eligible: boolean;
  mode:
    | 'off'
    | 'dry_run'
    | 'controlled_apply_locked'
    | 'blocked'
    | 'eligible_for_dry_run'
    | string;
  reasons?: string[];
  blocking_reasons?: string[];
  warnings?: string[];
  recommended_action?: string;
  requires_review?: boolean;
  blocks_autopublish?: boolean;
  publish_behavior_enabled?: boolean;
  controlled_apply_actionable?: boolean;
  settings_version?: number;
  customer_message?: string;
  evaluated_at?: string | null;
  manual_review_fallback_enabled?: boolean;
  kill_switch_summary?: LscArchiveAutopublishKillSwitchSummary;
}

/** AP-3 customer-safe archive autopublish dry-run preview read model. */
export interface LscArchiveAutopublishDryRunPreview {
  preview_available: boolean;
  preview_mode:
    | 'off'
    | 'dry_run'
    | 'blocked'
    | 'eligible_for_dry_run'
    | 'review_required'
    | 'controlled_apply_locked'
    | 'paused'
    | string;
  would_publish_if_controlled_apply_enabled?: boolean;
  would_route_to_review?: boolean;
  publish_behavior_enabled?: boolean;
  controlled_apply_actionable?: boolean;
  dry_run_only?: boolean;
  candidate_summary?: {
    matchup_label?: string | null;
    broadcaster_display?: string | null;
    review_status?: string | null;
    archive_status?: string | null;
    candidate_status?: string | null;
    match_source?: string | null;
    verified_live_handoff?: boolean;
    live_truth_status?: string | null;
  };
  eligibility_summary?: {
    eligible?: boolean;
    mode?: string;
    blocking_reasons?: string[];
    warnings?: string[];
    recommended_action?: string | null;
    requires_review?: boolean;
  };
  blocking_reasons?: string[];
  warnings?: string[];
  recommended_action?: string;
  manual_review_fallback_enabled?: boolean;
  evaluated_at?: string | null;
  settings_version?: number;
  customer_message?: string;
  no_publish_will_happen?: boolean;
  kill_switch_summary?: LscArchiveAutopublishKillSwitchSummary;
}

/** AP-4 customer-safe archive autopublish kill switch read model. */
export interface LscArchiveAutopublishKillSwitchSummary {
  kill_switch_active?: boolean;
  global_disabled?: boolean;
  league_disabled?: boolean;
  candidate_hold_active?: boolean;
}

export interface LscArchiveAutopublishKillSwitch extends LscArchiveAutopublishKillSwitchSummary {
  disabled_reason_codes?: string[];
  customer_safe_reason?: string;
  recommended_action?: string;
  publish_behavior_enabled?: boolean;
  controlled_apply_actionable?: boolean;
  evaluated_at?: string | null;
}

/** AP-4 archive autopublish audit event shape (scaffolding only). */
export interface LscArchiveAutopublishAuditEvent {
  event_type?: string;
  league_id?: string;
  candidate_ref?: string | null;
  actor_uid?: string | null;
  source?: string;
  decision?: string | null;
  reason_codes?: string[];
  publish_behavior_enabled?: boolean;
  controlled_apply_actionable?: boolean;
  created_at?: string | null;
  settings_version?: number;
  eligibility_version?: number;
  audit_schema_version?: number;
}

export interface LscLiveAutomationPolicy {
  auto_show_verified_live_games?: boolean;
  minimum_live_confidence?: number;
  schedule_corrected_live_behavior?: string;
  minimum_schedule_corrected_live_confidence?: number;
  unknown_matchup_behavior?: string;
  auto_create_live_needs_attention_exceptions?: boolean;
  policy_version?: number;
  updated_by?: string | null;
  updated_at?: string | null;
  policy_change_reason?: string | null;
}

export interface LscLeague {
  id: string;
  slug: string;
  name: string;
  game_key?: string;
  game_label?: string;
  commissioner_uid?: string;
  visibility?: string;
  league_tag?: string | null;
  status?: string;
  archived_at?: string | null;
  archived_by_uid?: string | null;
  restored_at?: string | null;
  restored_by_uid?: string | null;
  created_at?: string;
  updated_at?: string;
  logo_url?: string | null;
  logo_uploaded_at?: string | null;
  active_season_label?: string | null;
  active_edition?: string | null;
  active_stage_label?: string | null;
  season_phase?: string | null;
  active_snapshot_id?: string | null;
  last_sync_at?: string | null;
  last_sync_source?: string | null;
  last_sync_status?: string | null;
  /** Which broadcaster leads a grouped live game. Members may read this. */
  streaming_obligation?: string | null;
  /** Stream home, games, and watch. Missing means off. */
  stream_pages?: 'off' | 'on';
  /** Present on the league detail payload for the founder only. */
  can_set_stream_pages?: boolean;
  external_neonsportz_slug?: string | null;
  schedule_source?: LscScheduleSource | null;
  archive_automation_policy?: LscArchiveAutomationPolicy;
  live_automation_policy?: LscLiveAutomationPolicy;
}

export interface LscScheduleSource {
  provider?: string | null;
  source_url?: string | null;
  provider_league_id?: string | null;
  provider_slug?: string | null;
  game?: string | null;
  platform?: string | null;
  enabled?: boolean;
  last_sync_at?: string | null;
  last_detected_season?: string | null;
  last_detected_week?: string | null;
  last_applied_season?: string | null;
  last_applied_week?: string | null;
  sync_status?: string | null;
  attention_holds?: string[];
  attention_message?: string | null;
  schedule_note?: string | null;
  schedule_verdict?: LscScheduleCheckVerdict | null;
}

/** Server-authored schedule check. The card renders verdict_text and does not invent one. */
export interface LscScheduleCheckVerdict {
  verdict_text?: string | null;
  verdict_checked_at?: string | null;
  verdict_next_check_at?: string | null;
  verdict_outcome?: 'failed' | null;
  verdict_reason?: string | null;
  games_here?: number | null;
  games_found?: number | null;
  verdict_git_sha?: string | null;
  verdict_contract_version?: number | null;
  verdict_current?: boolean;
}

export interface LscScheduleSourceStatus {
  success?: boolean;
  league_id?: string;
  pilot_enabled?: boolean;
  /** Kill switch: LSC_AUTO_SYNC_SAFE_APPLY_ENABLED on the backend. */
  auto_sync_safe_apply_available?: boolean;
  schedule_source?: LscScheduleSource | null;
  active_season_label?: string | null;
  active_stage_label?: string | null;
  source_state_stale?: boolean;
  source_state_stale_reason?: string | null;
  auto_sync_enabled?: boolean;
  auto_sync_safe_apply_enabled?: boolean;
}

export type LscScheduleStaleRepairState =
  | 'repaired'
  | 'already_current'
  | 'review_required'
  | 'rollover_review_required'
  | 'source_unavailable'
  | 'conflicts_require_review'
  | 'schedule_not_configured';

export interface LscScheduleStaleRepairResult {
  success?: boolean;
  state: LscScheduleStaleRepairState;
  message: string;
  source_state_stale?: boolean;
  repaired?: boolean;
  active_season_label?: string | null;
  active_stage_label?: string | null;
  detected_stage_label?: string | null;
  member_side_mismatch_count?: number;
}

export const SCHEDULE_REPAIR_STRUCTURED_ERROR_CODES = [
  'schedule_repair_not_configured',
  'schedule_repair_source_unavailable',
  'schedule_repair_preview_required',
  'schedule_repair_conflicts_require_review',
  'schedule_repair_forbidden',
  'schedule_repair_league_mismatch',
  'schedule_repair_stale_preview',
  'schedule_repair_no_changes',
  'schedule_repair_invalid_context',
  'schedule_repair_not_found',
] as const;

export type ScheduleRepairStructuredErrorCode =
  (typeof SCHEDULE_REPAIR_STRUCTURED_ERROR_CODES)[number];

export type ScheduleRepairRecommendedAction =
  | 'preview_repair'
  | 'connect_source'
  | 'review_conflicts'
  | 'import_review_apply'
  | 'no_action_needed';

export interface ScheduleRepairContext {
  season_label?: string | null;
  stage_label?: string | null;
  season_phase?: string | null;
  active_snapshot_id?: string | null;
  detected_season_label?: string | null;
  detected_stage_label?: string | null;
  source_state_stale?: boolean;
  is_stale?: boolean;
  snapshot_id?: string | null;
  source?: string | null;
}

export interface ScheduleRepairDiffSummary {
  games_to_add: number;
  games_to_update: number;
  games_to_remove: number;
  games_to_ignore: number;
  conflicts: number;
}

export interface ScheduleRepairItem {
  action: 'add' | 'update' | 'remove';
  match?: Record<string, unknown>;
  changed_fields?: string[];
}

export interface ScheduleRepairRun {
  repair_run_id: string;
  league_id?: string;
  source?: string;
  status?: string;
  repair_reason?: string;
  current_context?: ScheduleRepairContext;
  proposed_context?: ScheduleRepairContext;
  diff_summary?: ScheduleRepairDiffSummary;
  items?: ScheduleRepairItem[];
  safe_to_apply?: boolean;
  requires_review?: boolean;
  reason_codes?: string[];
  snapshot_id?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
  applied?: boolean;
  applied_at?: string | null;
  applied_by?: string | null;
  applied_counts?: ScheduleRepairDiffSummary & { matches_applied?: number };
}

export interface ScheduleRepairStatus {
  success?: boolean;
  league_id?: string;
  repair_needed: boolean;
  current_context?: ScheduleRepairContext;
  stale_signals?: string[];
  recommended_action?: ScheduleRepairRecommendedAction | string;
  can_preview?: boolean;
  can_apply?: boolean;
  blockers?: string[];
  source_state_stale?: boolean;
  is_stale?: boolean;
}

export interface ScheduleRepairPreviewRequest {
  source?: string;
  snapshot_id?: string;
  repair_reason?: string;
}

export interface ScheduleRepairPreviewResponse {
  success?: boolean;
  repair_run_id: string;
  current_context?: ScheduleRepairContext;
  proposed_context?: ScheduleRepairContext;
  diff_summary: ScheduleRepairDiffSummary;
  items?: ScheduleRepairItem[];
  safe_to_apply: boolean;
  requires_review: boolean;
  reason_codes?: string[];
  expires_at?: string | null;
  repair_reason?: string;
}

export interface ScheduleRepairApplyRequest {
  repair_run_id: string;
  acknowledge_review?: boolean;
}

export interface ScheduleRepairApplyResponse {
  success?: boolean;
  repair_run_id: string;
  applied: boolean;
  applied_counts?: ScheduleRepairDiffSummary & { matches_applied?: number };
  canonical_context?: ScheduleRepairContext;
  readiness_status?: string;
  audit?: Record<string, unknown>;
  active_season_label?: string | null;
  active_stage_label?: string | null;
  source_state_stale?: boolean;
}

export interface LscScheduleSourceSyncResult {
  success?: boolean;
  status?: string;
  dry_run?: boolean;
  applied?: boolean;
  stage_pointer_applied?: boolean;
  schedule_data_applied?: boolean;
  league_id?: string;
  schedule_source?: LscScheduleSource | null;
  active_season_label?: string | null;
  active_stage_label?: string | null;
  previous_stage_label?: string | null;
  new_stage_label?: string | null;
  detected_season_label?: string | null;
  detected_stage_label?: string | null;
  out_of_sync?: boolean;
  safe_to_apply?: boolean;
  hold_reasons?: string[];
  stage_pointer_apply_holds?: string[];
  schedule_data_apply_holds?: string[];
  remaining_schedule_holds?: string[];
  summary_copy?: string;
  schedule_verdict?: LscScheduleCheckVerdict | null;
  warnings?: string[];
  diff_summary?: Record<string, unknown> | null;
  would_apply_changes?: Record<string, unknown> | null;
  error_message_safe?: string | null;
  season_changed?: boolean;
  rollover_review_required?: boolean;
  rollover_preview_ready?: boolean;
  rollover_apply_ready?: boolean;
  apply_allowed?: boolean;
  conflicts_require_review?: boolean;
  team_assignment_review_required?: boolean;
  recommended_action?: string | null;
  games_detected_count?: number;
}

export interface LscScheduleDiffMatchSummary {
  match_id?: string | null;
  external_match_id?: string | null;
  season_label?: string | null;
  stage_label?: string | null;
  home_side_label?: string | null;
  away_side_label?: string | null;
  status?: string | null;
  scheduled_at?: string | null;
  home_score?: number | null;
  away_score?: number | null;
  vod_linked?: boolean;
  changed_fields?: string[];
  previous_status?: string | null;
}

export interface LscScheduleDiffSummary {
  new_matches: LscScheduleDiffMatchSummary[];
  updated_matches: LscScheduleDiffMatchSummary[];
  completed_matches: LscScheduleDiffMatchSummary[];
  removed_or_superseded_matches: LscScheduleDiffMatchSummary[];
  season_changed: boolean;
  edition_changed?: boolean;
  edition_undeclared?: boolean;
  stage_changed: boolean;
  incoming_season_label?: string | null;
  active_season_label?: string | null;
  incoming_edition?: string | null;
  active_edition?: string | null;
  incoming_stage_label?: string | null;
  active_stage_label?: string | null;
  member_side_mismatches: Array<{
    member_id?: string | null;
    display_name?: string | null;
    current_side_label?: string | null;
    incoming_side_label?: string | null;
  }>;
  missing_members_or_unmatched_participants: Array<{
    side_label?: string | null;
    team_id?: string | null;
    display_name?: string | null;
  }>;
  destructive_or_review_required_changes: string[];
  counts: {
    new_matches?: number;
    updated_matches?: number;
    completed_matches?: number;
    removed_or_superseded_matches?: number;
    member_side_mismatches?: number;
    missing_members_or_unmatched_participants?: number;
  };
}

export interface LscAutoSyncLeagueSummary {
  auto_sync_enabled?: boolean;
  auto_sync_safe_apply_enabled?: boolean;
  auto_sync_provider?: string | null;
  auto_sync_provider_league_id?: string | null;
  auto_sync_interval_minutes?: number | null;
  auto_sync_last_checked_at?: string | null;
  auto_sync_last_applied_at?: string | null;
  auto_sync_last_status?: string | null;
  auto_sync_last_error?: string | null;
  auto_sync_last_apply_summary?: string | null;
  auto_sync_last_hold_message?: string | null;
  source_state_fingerprint?: Record<string, unknown> | null;
  source_state_stale?: boolean;
  source_state_stale_reason?: string | null;
  auto_sync_lock_until?: string | null;
  provider_link_broken?: boolean;
  provider_link_next_check_at?: string | null;
  provider_link_suggestion?: {
    abbrev?: string | null;
    name?: string | null;
    madden_year?: number | string | null;
    provider_slug?: string | null;
  } | null;
  neonsportz_discord_id?: string | null;
}

export interface LscAutoSyncRun {
  id?: string;
  league_id?: string;
  triggered_by?: string;
  dry_run?: boolean;
  status?: string;
  operation_mode?: string;
  started_at?: string | null;
  finished_at?: string | null;
  idempotency_key?: string | null;
  provider?: string;
  provider_league_id?: string | null;
  safe_to_apply?: boolean | null;
  applied?: boolean;
  apply_requested?: boolean;
  hold_reasons?: string[];
  error_code?: string | null;
  error_message_safe?: string | null;
  diff_summary?: Record<string, unknown> | null;
  applied_changes?: Record<string, unknown> | null;
  would_apply_changes?: Record<string, unknown> | null;
  source_state_before?: Record<string, unknown>;
  source_state_after?: Record<string, unknown>;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface LscGuardedAutoSyncApplyStatus {
  eligible: boolean;
  mode: string;
  reasons: string[];
  customer_message: string;
  blocks_launch: boolean;
  blocks_automation: boolean;
  recommended_action?: string | null;
  preview_allowed: boolean;
  apply_allowed: boolean;
  dry_run_only: boolean;
  same_season_only: boolean;
  blind_rollover_blocked: boolean;
}

export interface LscSyncStatus {
  league_id?: string;
  last_sync_at?: string | null;
  last_sync_source?: string | null;
  last_sync_status?: string | null;
  active_season_label?: string | null;
  active_stage_label?: string | null;
  season_phase?: string | null;
  active_snapshot_id?: string | null;
  external_neonsportz_slug?: string | null;
  current_open_matches_count: number;
  provider_week?: number | string | null;
  provider_stage?: string | null;
  provider_stage_label?: string | null;
  provider_season_index?: number | string | null;
  detected_season_label?: string | null;
  detected_stage_label?: string | null;
  season_changed?: boolean;
  rollover_review_required?: boolean;
  recommended_action?: string | null;
  source_state_stale?: boolean;
  source_state_stale_reason?: string | null;
  is_stale: boolean;
  schedule_verdict?: LscScheduleCheckVerdict | null;
  provider_link_message?: string | null;
  provider_link_broken?: boolean;
  provider_link_suggestion?: {
    abbrev?: string | null;
    name?: string | null;
    madden_year?: number | string | null;
    provider_slug?: string | null;
  } | null;
  held_for_review?: boolean;
  warnings: string[];
  missing_twitch_count: number;
  imported_unclaimed_count?: number;
  member_side_mismatch_count: number;
  auto_sync?: LscAutoSyncLeagueSummary;
  schedule_source?: LscScheduleSource | null;
  guarded_auto_sync_apply?: LscGuardedAutoSyncApplyStatus | null;
}

export type LscLiveReadinessOverallStatus = 'ready' | 'warning' | 'blocked';

export interface LscLiveReadinessAction {
  code: string;
  label: string;
  href_path: string;
}

export interface LscLiveReadinessMember {
  member_id?: string | null;
  display_name?: string | null;
  status?: string | null;
  current_side_label?: string | null;
  twitch_login?: string | null;
  twitch_user_id?: string | null;
  has_eventsub_subscription: boolean;
  eventsub_online_status?: string | null;
  eventsub_offline_status?: string | null;
  open_match_count: number;
  eligible_match_ids: string[];
  readiness_status: LscLiveReadinessOverallStatus;
  blockers: string[];
  warnings: string[];
}

export interface LscLiveReadinessReport {
  overall_status: LscLiveReadinessOverallStatus;
  league_id?: string;
  checked_at?: string;
  stream_domain_status: {
    eventsub_enabled: boolean;
    callback_url_configured: boolean;
    frontend_host?: string;
    status: 'ok' | 'blocked';
  };
  schedule_sync_status: {
    last_sync_at?: string | null;
    last_sync_source?: string | null;
    last_sync_status?: string | null;
    external_neonsportz_slug?: string | null;
  };
  active_season_label?: string | null;
  active_stage_label?: string | null;
  provider_stage_label?: string | null;
  is_schedule_stale: boolean;
  current_open_matches_count: number;
  eligible_live_game_members_count: number;
  members_missing_twitch_count: number;
  members_missing_side_assignment_count: number;
  member_side_mismatch_count: number;
  eventsub_subscription_status: {
    status: LscLiveReadinessOverallStatus;
    active_members_with_twitch: number;
    members_with_enabled_pair: number;
    members_missing_online: number;
    members_missing_offline: number;
    members_pending_verification: number;
    members_failed: number;
    live_detection_state?: 'ready' | 'provisioning' | 'failed' | 'missing_twitch' | null;
  };
  live_state_status: {
    status: 'idle' | 'live';
    live_member_count: number;
    qualified_live_games_count: number;
  };
  test_candidate_members: LscLiveReadinessMember[];
  blockers: string[];
  warnings: string[];
  next_actions: LscLiveReadinessAction[];
}

export interface LscLiveSubscription {
  twitch_user_id: string;
  twitch_login?: string | null;
  event_type: string;
  status: string;
  subscription_id?: string | null;
  league_ids: string[];
  member_ids_by_league: Record<string, string>;
  last_sync_at?: string | null;
  last_error?: string | null;
}

export interface LscLiveSubscriptionSyncRequest {
  dry_run?: boolean;
  force_reverify?: boolean;
  reconcile_enabled?: boolean;
}

export interface LscLiveSubscriptionSyncSummary {
  dry_run: boolean;
  force_reverify?: boolean;
  reconcile_enabled?: boolean;
  eligible_members: number;
  unique_broadcasters: number;
  members_skipped: number;
  subscriptions_created: number;
  subscriptions_existing: number;
  subscriptions_existing_pending?: number;
  subscriptions_stale_pending?: number;
  subscriptions_would_reverify?: number;
  subscriptions_would_mark_enabled?: number;
  subscriptions_reconciled_enabled?: number;
  subscriptions_reverified?: number;
  subscriptions_would_recreate_missing?: number;
  subscriptions_recreated_missing?: number;
  subscriptions_failed: number;
}

export interface LscLiveSubscriptionSyncItem {
  member_id?: string | null;
  twitch_user_id?: string | null;
  twitch_login?: string | null;
  event_type?: string | null;
  action: string;
  subscription_id?: string | null;
  status?: string | null;
  error?: string | null;
}

export interface LscLiveSubscriptionSyncResult {
  success: boolean;
  league_id: string;
  summary: LscLiveSubscriptionSyncSummary;
  items: LscLiveSubscriptionSyncItem[];
}

export interface LscMemberIdentity {
  identity_type?: string;
  identity_value?: string;
  display_label?: string;
  source?: string;
  verified?: boolean;
}

export type LscTeamAssignmentStatus = {
  conflict: boolean;
  state:
    | 'open_conflict'
    | 'resolved'
    | 'custom_external_divergence'
    | 'external_missing'
    | 'unassigned'
    | 'no_conflict'
    | string;
  lsc_label?: string | null;
  external_label?: string | null;
  external_source?: string | null;
  external_missing?: boolean;
  is_custom?: boolean;
};

export interface LscLeagueDetail {
  league: LscLeague;
  viewerIsCommissioner: boolean;
  viewerIsOwner?: boolean;
  viewerIsOperator?: boolean;
  effectiveRole?: 'owner' | 'commissioner' | 'member';
  membership: LscMember | null;
  effectiveMembership: LscMember | null;
}

export interface LscStreamAccount {
  platform: 'twitch' | 'youtube' | string;
  account_id?: string | null;
  handle?: string | null;
  primary?: boolean;
}

export interface LscMember {
  id: string;
  league_id: string;
  user_uid?: string | null;
  display_name: string;
  role: string;
  status?: string;
  stream_provider?: string | null;
  stream_platform?: 'twitch' | 'youtube' | string | null;
  stream_accounts?: LscStreamAccount[] | null;
  roster_platform?: 'twitch' | 'youtube' | string | null;
  roster_handle?: string | null;
  roster_account_bound?: boolean;
  detection_covered?: boolean;
  google_email?: string | null;
  twitch_user_id?: string | null;
  twitch_login?: string | null;
  provider_user_id?: string | null;
  provider_login?: string | null;
  discord_username?: string | null;
  discord_id?: string | null;
  discord_handle?: string | null;
  identities?: LscMemberIdentity[];
  current_side_label?: string | null;
  source_metadata?: Record<string, unknown>;
  team_assignment_status?: LscTeamAssignmentStatus | null;
  merged_into_member_id?: string | null;
  deactivation_reason?: string | null;
  deactivated_at?: string | null;
  deactivated_by_uid?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LscMemberInviteSummary {
  id: string;
  league_id: string;
  member_id: string;
  status: 'active' | 'expired' | 'revoked' | 'claimed' | 'invalid' | string;
  valid: boolean;
  created_at?: string | null;
  expires_at?: string | null;
  claimed_at?: string | null;
  claimed_by_uid?: string | null;
  claimed_twitch_login?: string | null;
  claimed_twitch_user_id?: string | null;
  unclaimed_at?: string | null;
  unclaimed_by_uid?: string | null;
  revoked_at?: string | null;
  claim_url?: string | null;
}

export interface LscMemberInviteBulkRow {
  member_id: string;
  display_name: string;
  team_label?: string | null;
  claim_url: string;
  expires_at?: string | null;
  reused?: boolean;
}

export interface LscMemberInviteIssueResult {
  success: boolean;
  invite: LscMemberInviteSummary;
  claim_url: string;
}

export interface LscMemberInvitePreview {
  success?: boolean;
  valid: boolean;
  reason?: string;
  message?: string;
  headline?: string;
  member_display_name?: string;
  team_label?: string | null;
  league_name?: string;
  league_id?: string;
  member_id?: string;
  stream_platform?: 'twitch' | 'youtube' | string | null;
  sign_in_method?: 'google' | 'twitch';
  requires_twitch?: boolean;
  youtube_signin_available?: boolean;
  primary_stream_handle?: string | null;
}

export interface LscMemberInviteClaimResult {
  success: boolean;
  league_id: string;
  member_id: string;
  league_path: string;
  role: string;
  status: string;
  twitch_login?: string | null;
  twitch_user_id?: string | null;
  user_uid?: string | null;
}

export interface LscMemberCounts {
  current_member_count: number;
  active_linked_member_count: number;
  pending_link_member_count: number;
  inactive_member_count: number;
  invited_member_count?: number;
  total_member_count: number;
  team_assignment_conflict_count?: number;
}

export interface LscMembersListResult {
  members: LscMember[];
  counts: LscMemberCounts;
}

export interface LscMemberTeamOption {
  label: string;
  normalized_label?: string;
  source_team_id?: string | null;
  source?: string;
  claimed_by_member_id?: string | null;
  claimed_by_display_name?: string | null;
  claimed_by_status?: string | null;
  is_claimed?: boolean;
  is_current_member_assignment?: boolean;
}

export interface LscMemberTeamOptionsResult {
  success: boolean;
  league_id: string;
  snapshot_id?: string | null;
  derived_from: string[];
  unassigned_allowed: boolean;
  custom_allowed: boolean;
  teams: LscMemberTeamOption[];
}

export interface LscProviderSyncMemberRef {
  member_id: string;
  display_name?: string | null;
  status?: string | null;
  current_side_label?: string | null;
  twitch_login?: string | null;
  twitch_user_id?: string | null;
}

export type LscProviderSyncRowState =
  | 'confirmed'
  | 'suggested'
  | 'member_has_other_team'
  | 'ambiguous'
  | 'no_member';

export type LscProviderSyncMatchBasis = 'discord_id' | 'in_game_name';

export interface LscProviderSyncRow {
  team_label: string;
  team_id?: string | null;
  team_abbr?: string | null;
  provider_owner_name?: string | null;
  state: LscProviderSyncRowState;
  member?: LscProviderSyncMemberRef | null;
  match_basis?: LscProviderSyncMatchBasis | null;
  candidates?: LscProviderSyncMemberRef[];
  provider_suggests?: (LscProviderSyncMemberRef & { match_basis?: LscProviderSyncMatchBasis | null }) | null;
  message?: string | null;
}

export interface LscMemberProviderSyncResult {
  success: boolean;
  league_id: string;
  state: 'ready' | 'no_active_season' | 'no_provider_snapshot';
  message?: string | null;
  active_season_label?: string | null;
  active_edition?: string | null;
  scope_label?: string | null;
  provider_league?: string | null;
  synced_label?: string | null;
  synced_at?: string | null;
  snapshot_stage_label?: string | null;
  unassigned_member_count: number;
  provider_teams_without_member_count: number;
  suggestion_count: number;
  rows: LscProviderSyncRow[];
}

export interface LscMatch {
  id: string;
  league_id?: string;
  snapshot_id?: string;
  external_match_id?: string;
  game_key?: string;
  season_label?: string;
  edition?: string | null;
  stage_label?: string;
  home_side_label?: string;
  away_side_label?: string;
  home_identity_value?: string;
  away_identity_value?: string;
  scheduled_at?: string;
  window_start?: string;
  window_end?: string;
  status?: string;
  lifecycle_status?: string;
  vod_linked?: boolean;
  linked_vod_archive_ids?: string[];
  home_score?: number | null;
  away_score?: number | null;
  source_metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export type LscReviewAction = 'approve' | 'reject' | 'dismiss';

export type LscVodIgnoreReason =
  | 'not_league_game'
  | 'duplicate'
  | 'stale'
  | 'wrong_broadcaster'
  | 'wrong_league'
  | 'too_short'
  | 'test_stream'
  | 'other';
export type LscReviewStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'published'
  | 'unpublished'
  | string;
export type LscArchiveStatus = 'published' | 'unpublished' | 'removed' | string;
export type LscArchiveVisibility = 'league_members' | 'commissioner_only' | string;

export type LscTruthStatus =
  | 'unverified'
  | 'verified'
  | 'likely'
  | 'inconclusive'
  | 'conflict';

export interface LscVodCandidate {
  id: string;
  league_id?: string;
  member_id?: string;
  user_uid?: string | null;
  stream_provider?: string;
  provider_vod_id?: string;
  provider_broadcaster_login?: string;
  provider_broadcaster_display_name?: string;
  title?: string;
  description?: string;
  created_at?: string;
  published_at?: string;
  duration_seconds?: number | null;
  raw_duration?: string;
  url?: string;
  thumbnail_url?: string;
  viewable?: string;
  candidate_status?: string;
  availability_status?: string;
  unavailable_reason?: string;
  availability_checked_at?: string;
  availability_source?: string;
  match_status?: string;
  match_source?: string | null;
  competition_match_id?: string | null;
  match_confidence?: number | null;
  best_match_confidence_band?: string | null;
  match_reason_codes?: string[];
  matched_at?: string | null;
  review_status?: LscReviewStatus;
  archive_id?: string | null;
  archive_status?: string | null;
  published_at_lsc?: string | null;
  metadata_fetched_at?: string;
  updated_at?: string;
  sync_source?: string | null;
  sync_run_id?: string | null;
  source_metadata?: {
    last_sync_source?: string | null;
    last_sync_run_id?: string | null;
    last_sync_at?: string | null;
    [key: string]: unknown;
  } | null;
  truth_status?: LscTruthStatus;
  truth_checked_at?: string | null;
  truth_method?: string | null;
  truth_evidence?: Record<string, unknown> | null;
  truth_detected_teams?: string[];
  truth_detected_gamer_tags?: string[];
  truth_confidence?: string | null;
  truth_cost_estimate?: number | null;
  truth_gameplay_verification_status?: string | null;
  truth_league_match_resolution?: string | null;
  commissioner_publish?: {
    allowed: boolean;
    teams_match: boolean;
    match_confirmed: boolean;
    reason?: string | null;
    warning?: string | null;
    headline: string;
    confirm?: LscPublishConfirm | null;
  } | null;
  truth_recommended_action?: string | null;
  truth_league_resolution_summary?: string | null;
  truth_schedule_binding_status?: LscScheduleBindingStatus | null;
  truth_bound_match_id?: string | null;
  truth_timeline_label?: string | null;
  truth_schedule_binding_reason_codes?: string[];
  truth_reason_codes?: string[];
  truth_commissioner_override_count?: number | null;
  truth_identity_verified_by_commissioner?: boolean | null;
  truth_empty_hud_handoff_attestation_count?: number | null;
  truth_empty_hud_handoff_attested_by_commissioner?: boolean | null;
  truth_verification_status?: string | null;
  truth_verification_progress_label?: string | null;
  truth_scan_refused_message?: string | null;
}

export interface LscAutoVerifyEligibilityCandidate {
  candidate_id: string;
  eligibility_class: string;
  eligible_for_preview: boolean;
  eligible_for_execute: boolean;
  recommended_next_action: string;
  match_context_source: string;
  requires_commissioner_action: boolean;
  confidence_band?: string | null;
  reasons: string[];
  blockers: string[];
}

export interface LscAutoVerifyEligibilityReport {
  success: boolean;
  league_id: string;
  generated_at: string;
  dry_run: boolean;
  execute_enabled: boolean;
  config: {
    limit?: number;
    sample_limit?: number;
    require_live_handoff?: boolean;
    min_matcher_confidence?: number;
    min_duration_seconds?: number;
    max_vod_age_days?: number;
  };
  summary: {
    total_candidates: number;
    preview_eligible_count: number;
    execute_eligible_count: number;
    blocked_count: number;
    counts_by_class: Record<string, number>;
  };
  samples_by_class: Record<string, unknown[]>;
  disclaimers: string[];
  candidates?: LscAutoVerifyEligibilityCandidate[];
}

export interface LscCommissionerIdentityOverride {
  raw_tag?: string;
  detected_side?: string;
  detected_side_label?: string;
  member_id?: string;
  member_display_name?: string;
  member_in_game_name?: string;
  member_status?: string;
  override_type?: string;
  scope?: string;
  created_by_user_uid?: string;
  created_by_display_name?: string;
  created_at?: string;
  note?: string | null;
  review_event_id?: string;
  reason_code?: string;
}

export type LscScheduleBindingStatus =
  | 'bound'
  | 'ambiguous'
  | 'unbound'
  | 'not_applicable';

export interface LscScheduleBindingCandidateMatch {
  match_id: string;
  season_label?: string;
  edition?: string | null;
  stage_label?: string;
  away_side_label?: string;
  home_side_label?: string;
  scheduled_at?: string | null;
  vod_linked?: boolean;
  timeline_label?: string;
  score?: number;
  rank_reason_codes?: string[];
  date_distance_hours?: number;
  is_fallback?: boolean;
  is_reversed_orientation?: boolean;
}

export interface LscTruthScheduleBinding {
  schedule_binding_status?: LscScheduleBindingStatus;
  bound_match_id?: string | null;
  timeline_label?: string;
  home_away_match?: { away?: string | null; home?: string | null };
  candidate_matches?: LscScheduleBindingCandidateMatch[];
  primary_matches?: LscScheduleBindingCandidateMatch[];
  fallback_matches?: LscScheduleBindingCandidateMatch[];
  binding_reason_codes?: string[];
}

export interface LscVodMatchSuggestion {
  id: string;
  league_id?: string;
  candidate_id?: string;
  competition_match_id?: string;
  score?: number;
  confidence_band?: string;
  reason_codes?: string[];
  warnings?: string[];
  hard_reject_reasons?: string[];
  matched_member_id?: string;
  matched_side?: string;
  matcher_version?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  source_metadata?: Record<string, unknown>;
  match_summary?: LscMatchSummary | null;
  timeline_label?: string | null;
}

export interface LscMatchSummary {
  season_label?: string;
  edition?: string | null;
  stage_label?: string;
  home_side_label?: string;
  away_side_label?: string;
  home_identity_value?: string;
  away_identity_value?: string;
  scheduled_at?: string;
  game_key?: string;
}

// --- Needs Attention (exception-based archive automation) ---

export type LscNeedsAttentionAction =
  | 'approve_to_archive'
  | 'edit_matchup'
  | 'dismiss';

export interface LscNeedsAttentionEvidenceSummary {
  live_game_verified?: boolean;
  live_game_side_pending?: boolean;
  side_pending_copy?: string;
  side_confirmation?: {
    available?: boolean;
    home_team_label?: string | null;
    away_team_label?: string | null;
  };
  matchup_linked?: boolean;
  streamer_verified?: boolean;
  archive_verification_complete?: boolean;
  confidence_label?: string;
  season_label?: string;
  week_label?: string;
  automation_ready?: boolean;
  gameplay_detected?: boolean;
  scoreboard_detected?: boolean;
  schedule_out_of_sync?: boolean;
}

export interface LscNeedsAttentionPlayback {
  provider?: string;
  twitch_video_id?: string | null;
  channel_login?: string | null;
  thumbnail_url?: string | null;
  watch_url?: string | null;
  title?: string | null;
  duration_seconds?: number | null;
  is_live?: boolean;
}

export type LscConfidenceBand = 'strong' | 'needs_review' | 'weak' | 'insufficient';

export interface LscPublishConfirm {
  matchup?: string | null;
  when_label?: string | null;
  broadcaster?: string | null;
  teams_read?: string[];
  gamertag_warning?: string | null;
  broadcaster_warning?: string | null;
  frame_confirmation?: 'confirmed' | 'not_confirmed' | null;
}

export interface LscNeedsAttentionItem {
  item_key: string;
  item_type?: string | null;
  is_live_exception?: boolean;
  state?: string | null;
  status_label?: string | null;
  matchup_label: string;
  season_label?: string | null;
  week_label?: string | null;
  streamer_display: string;
  exception_reason: string;
  recommended_action: string;
  exception_code?: string | null;
  confidence_score?: number | null;
  confidence_band?: LscConfidenceBand | null;
  confidence_band_label?: string | null;
  evidence_summary?: LscNeedsAttentionEvidenceSummary;
  available_actions: LscNeedsAttentionAction[];
  publish_confirm?: LscPublishConfirm | null;
  competition_match_id?: string | null;
  candidate_review_path?: string | null;
  playback?: LscNeedsAttentionPlayback | null;
}

export interface LscNeedsAttentionMatchOption {
  match_id: string;
  matchup_label: string;
  away_team?: string | null;
  home_team?: string | null;
  season_label?: string | null;
  week_label?: string | null;
  scheduled_at?: string | null;
  is_suggested: boolean;
  suggestion_rank?: number | null;
}

export interface LscNeedsAttentionMatchOptions {
  item_key: string;
  current_match_id?: string | null;
  suggestions: LscNeedsAttentionMatchOption[];
  matches: LscNeedsAttentionMatchOption[];
  filters: {
    seasons: string[];
    weeks: string[];
    teams: string[];
  };
}

export interface LscNeedsAttentionReassignResult {
  success: boolean;
  status?: string;
  competition_match_id?: string | null;
  result_summary?: {
    review_status?: string | null;
    competition_match_id?: string | null;
    archive_status?: string | null;
  } | null;
}

export interface LscReviewQueueItem extends LscVodCandidate {
  best_suggestion?: LscVodMatchSuggestion | null;
  match_summary?: LscMatchSummary | null;
  member?: {
    id?: string;
    display_name?: string;
    twitch_login?: string;
    current_side_label?: string;
  };
}

export interface LscArchiveItem {
  id: string;
  league_id?: string;
  provider_vod_id?: string;
  stream_provider?: string;
  competition_match_id?: string;
  published_by_uid?: string;
  approved_by_uid?: string;
  approved_at?: string;
  published_at?: string;
  archive_status?: LscArchiveStatus;
  visibility?: LscArchiveVisibility;
  title?: string;
  description?: string;
  url?: string;
  embed_url?: string;
  embed_provider?: string;
  embed_parent_domains?: string[];
  thumbnail_url?: string;
  provider_broadcaster_login?: string;
  provider_broadcaster_display_name?: string;
  provider_broadcaster_id?: string;
  broadcaster_side?: 'home' | 'away' | null;
  /** Present on ``group=match`` cards. Index 0 is the lead. Do not reorder. */
  sources?: LscArchiveItem[];
  duration_seconds?: number | null;
  vod_created_at?: string;
  match_summary?: LscMatchSummary;
  match_confidence?: number | null;
  match_reason_codes?: string[];
  publish_source?: string | null;
  autopilot_published?: boolean;
  confidence_score?: number | null;
  confidence_band?: LscConfidenceBand | null;
  playback_status?: 'available' | 'unavailable' | 'unknown';
  playback_unavailable_reason?: string | null;
  playback_checked_at?: string;
  playback_source?: string;
  created_at?: string;
  updated_at?: string;
  /** RTTS gate fields (PR #143) — present on archive list/detail when backend is current. */
  rtts_entitled?: boolean;
  can_generate?: boolean;
  existing_reel?: {
    job_id: string;
    status: string;
    signed_url?: string | null;
    expires_at?: string | null;
    reel_duration_sec?: number | null;
  } | null;
  [key: string]: unknown;
}

export interface LscReviewResponse {
  success: boolean;
  candidate?: LscVodCandidate;
  suggestion_id?: string;
}

export interface LscIgnoreVodCandidatePayload {
  reason: LscVodIgnoreReason;
  note?: string;
}

export interface LscIgnoreVodCandidateResponse {
  success: boolean;
  candidate?: LscVodCandidate;
  audit_event?: Record<string, unknown>;
  idempotent?: boolean;
}

export interface LscPublishResponse {
  success: boolean;
  archive?: LscArchiveItem;
  candidate?: LscVodCandidate;
  idempotent?: boolean;
}

export interface LscPublishConflict {
  success: false;
  conflict: true;
  message: string;
  archive_id?: string;
}

export interface LscReviewEvent {
  id?: string;
  action?: string;
  actor_uid?: string;
  created_at?: string;
  note?: string;
}

export interface LscSyncRunWarning {
  code?: string;
  member_id?: string;
  display_name?: string;
  message?: string;
}

export interface LscSyncRunSummary {
  sync_run_id?: string;
  league_id?: string;
  status?: string;
  members_scanned?: number;
  members_skipped?: number;
  vods_seen?: number;
  vods_created?: number;
  vods_updated?: number;
  warnings?: LscSyncRunWarning[];
  errors?: LscSyncRunWarning[];
  dry_run?: boolean;
  created_after?: string;
  max_per_member?: number;
  candidates_preview?: LscVodCandidate[];
  readiness?: LscVodSyncReadiness;
}

export type LscVodSyncReadinessReason =
  | 'sync_ready'
  | 'inactive'
  | 'pending_link'
  | 'missing_twitch'
  | 'not_active'
  | 'unknown';

export interface LscVodSyncReadinessMember {
  member_id?: string;
  display_name?: string;
  current_side_label?: string | null;
  status?: string;
  reason?: LscVodSyncReadinessReason;
  action?: string;
  twitch_login?: string | null;
  twitch_user_id?: string | null;
}

export interface LscVodSyncReadiness {
  eligible_count: number;
  skipped_count: number;
  total_current_members: number;
  reason_counts?: Partial<Record<LscVodSyncReadinessReason, number>>;
  eligible_members?: LscVodSyncReadinessMember[];
  skipped_members?: LscVodSyncReadinessMember[];
}

export interface LscCsvImportResult {
  success: boolean;
  dry_run?: boolean;
  snapshot_id?: string;
  snapshot_preview?: Record<string, unknown>;
  warnings?: LscCsvImportIssue[] | string[];
  errors?: LscCsvImportIssue[] | string[];
  row_count?: number;
  match_count?: number;
  participant_count?: number;
  detected_format?: string;
  details?: LscCsvImportDetails;
  apply?: {
    matches_applied?: number;
    matches?: LscMatch[];
  };
  message?: string;
}

export interface LscNeonsportzPublishedSeason {
  season_index: number;
  stored_label: string;
  display_label: string;
  game_count: number;
  current: boolean;
}

export interface LscNeonsportzImportCounts {
  teams?: number;
  games?: number;
  fetched_teams?: number;
  fetched_games?: number;
  source_games_count?: number;
  provider_total_teams?: number;
  provider_total_games?: number;
  preview_displayed_games?: number;
  teams_truncated?: boolean;
  games_truncated?: boolean;
  truncated?: boolean;
  season_index?: number;
  season_filter_applied?: boolean;
  min_week_detected?: number | null;
  max_week_detected?: number | null;
  matches_by_week?: Record<string, number>;
  schedule_fetch_complete?: boolean;
  duplicate_games_skipped?: number;
  raw_fetched_games?: number;
  season_wide_raw_count?: number;
  week_scoped_fetch?: {
    added_games_count?: number;
  };
}

export interface LscNeonsportzScheduleCompleteness {
  season_index?: number;
  season_filter_applied?: boolean;
  min_week_detected?: number | null;
  max_week_detected?: number | null;
  schedule_fetch_complete?: boolean;
}

export interface LscNeonsportzLeagueMeta {
  name?: string;
  abbrev?: string;
  imported?: boolean;
  season?: number;
  week?: number;
  stage?: number;
  madden_year?: number | string | null;
  calendar_year?: number | string | null;
  last_updated?: string;
}

export interface LscNeonsportzParticipantPreview {
  side_label?: string;
  side_abbr?: string;
  discord_id?: string | null;
  discord_username?: string | null;
  discord_avatar_url?: string | null;
  discord_id_status?: string;
  in_game_name?: string | null;
  twitch_login?: string | null;
  status?: string;
}

export interface LscNeonsportzMatchPreview {
  external_match_id?: string;
  stage_label?: string;
  home_side_label?: string;
  away_side_label?: string;
  home_score?: number | null;
  away_score?: number | null;
  status?: string;
  scheduled_at?: string | null;
}

export interface LscNeonsportzUrlPreviewResult {
  success: boolean;
  dry_run?: boolean;
  detected_format?: string;
  provider?: string;
  slug?: string;
  league?: LscNeonsportzLeagueMeta;
  counts?: LscNeonsportzImportCounts;
  schedule_completeness?: LscNeonsportzScheduleCompleteness;
  participants_preview?: LscNeonsportzParticipantPreview[];
  matches_preview?: LscNeonsportzMatchPreview[];
  warnings?: LscCsvImportIssue[] | string[];
  errors?: LscCsvImportIssue[] | string[];
  requires_mapping?: boolean;
  message?: string;
  error?: string;
  snapshot_preview?: Record<string, unknown>;
  match_count?: number;
  participant_count?: number;
  summary?: {
    match_count?: number;
    participant_count?: number;
    season_label?: string;
    current_stage?: string;
  };
  diff_summary?: LscScheduleDiffSummary;
}

export interface LscNeonsportzUrlConfirmResult {
  success: boolean;
  dry_run?: boolean;
  job_id?: string;
  import_status?: 'queued' | 'running' | 'succeeded' | 'failed' | string;
  snapshot_id?: string;
  snapshot?: Record<string, unknown>;
  slug?: string;
  provider?: string;
  counts?: LscNeonsportzImportCounts;
  schedule_completeness?: LscNeonsportzScheduleCompleteness;
  warnings?: LscCsvImportIssue[] | string[];
  errors?: LscCsvImportIssue[] | string[];
  message?: string;
  error?: string;
  apply?: {
    matches_applied?: number;
    applied_matches?: number;
    matches?: LscMatch[];
  };
  summary?: {
    match_count?: number;
    participant_count?: number;
    season_label?: string;
    current_stage?: string;
  };
}

export interface LscImportedMemberPromotionItem {
  side_label?: string;
  discord_username?: string | null;
  discord_id?: string | null;
  in_game_name?: string | null;
  proposed_status?: string;
  action?: string;
  reason?: string;
  existing_member_id?: string | null;
  existing_member_status?: string | null;
}

export interface LscImportedMemberPromotionResult {
  success?: boolean;
  source?: string;
  snapshot_id?: string;
  dry_run?: boolean;
  summary?: {
    participants_found?: number;
    would_create?: number;
    would_update?: number;
    would_skip?: number;
    created?: number;
    updated?: number;
    skipped?: number;
  };
  items?: LscImportedMemberPromotionItem[];
  warnings?: string[];
  message?: string;
  error?: string;
}

export interface LscMemberClaim {
  id?: string;
  claim_id?: string;
  league_id?: string;
  member_id?: string;
  claimant_uid?: string;
  claimant_twitch_login?: string | null;
  claimant_twitch_user_id?: string | null;
  claimant_discord_id?: string | null;
  claimant_discord_username?: string | null;
  imported_member_discord_id?: string | null;
  imported_member_discord_username?: string | null;
  imported_member_in_game_name?: string | null;
  imported_member_side_label?: string | null;
  confidence?: string;
  confidence_reasons?: string[];
  status?: string;
  requested_at?: string;
  decided_at?: string | null;
  decided_by_uid?: string | null;
  decision_note?: string | null;
  source_provider?: string;
}

export interface LscMemberClaimSuggestion {
  member_id?: string;
  side_label?: string | null;
  discord_username?: string | null;
  discord_id?: string | null;
  in_game_name?: string | null;
  status?: string;
  confidence?: string;
  confidence_reasons?: string[];
  claimable?: boolean;
  pending_claim?: LscMemberClaim | null;
}

export interface LscMemberClaimSuggestionsResult {
  success?: boolean;
  discord_connected?: boolean;
  suggestions?: LscMemberClaimSuggestion[];
  message?: string | null;
}

export interface LscMyClaimOpportunity {
  league_id?: string;
  league_name?: string;
  candidate_member_id?: string | null;
  current_side_label?: string | null;
  in_game_name?: string | null;
  game_identity?: string | null;
  source_provider?: string;
  match_type?: string;
  confidence?: string;
  claim_status?: string;
  claim_id?: string | null;
  can_request_claim?: boolean;
  can_open_league?: boolean;
  requested_at?: string | null;
  decided_at?: string | null;
  rejection_reason?: string | null;
}

export interface LscMyClaimOpportunitiesResult {
  success?: boolean;
  discord_connected?: boolean;
  opportunities?: LscMyClaimOpportunity[];
  count?: number;
  message?: string | null;
}

export interface LscMemberClaimSubmitResult {
  success?: boolean;
  claim?: LscMemberClaim;
  existing?: boolean;
  message?: string;
  error?: string;
}

export interface LscMemberClaimListResult {
  success?: boolean;
  claims?: LscMemberClaim[];
  count?: number;
}

export interface LscMemberClaimDecisionResult {
  success?: boolean;
  claim?: LscMemberClaim;
  member?: LscMember;
  message?: string;
  error?: string;
}

export interface LscLeagueInvite {
  id?: string;
  invite_id?: string;
  league_id?: string;
  code?: string;
  status?: string;
  stored_status?: string;
  role?: string;
  source?: string;
  created_by_uid?: string;
  created_by_twitch_login?: string | null;
  created_at?: string;
  updated_at?: string;
  expires_at?: string | null;
  max_uses?: number | null;
  used_count?: number;
  remaining_uses?: number | null;
  revoked_at?: string | null;
  revoked_by_uid?: string | null;
  revoked_reason?: string | null;
  share_path?: string | null;
  invite_url?: string | null;
}

export interface LscLeagueInviteListResult {
  success?: boolean;
  invites?: LscLeagueInvite[];
  count?: number;
}

export interface LscLeagueInviteCreateResult {
  success?: boolean;
  invite?: LscLeagueInvite;
  message?: string;
  error?: string;
}

export interface LscLeagueInvitePreviewResult {
  success?: boolean;
  valid?: boolean;
  invite_status?: string;
  league_id?: string;
  league_name?: string;
  league_slug?: string;
  expires_at?: string | null;
  remaining_uses?: number | null;
  can_accept?: boolean;
  already_member?: boolean;
  pending_request?: boolean;
  request?: LscJoinRequest;
  message?: string;
  error?: string;
}

export interface LscJoinRequest {
  id?: string;
  request_id?: string;
  league_id?: string;
  league_name?: string;
  league_slug?: string;
  user_uid?: string;
  twitch_login?: string | null;
  twitch_user_id?: string | null;
  discord_id?: string | null;
  discord_username?: string | null;
  discord_avatar_url?: string | null;
  status?: string;
  source?: string;
  invite_id?: string;
  invite_code?: string;
  requested_role?: string;
  created_at?: string;
  updated_at?: string;
  decided_at?: string | null;
  decided_by_uid?: string | null;
  decision_note?: string | null;
}

export interface LscJoinRequestListResult {
  success?: boolean;
  requests?: LscJoinRequest[];
  count?: number;
}

export interface LscJoinRequestDecisionResult {
  success?: boolean;
  request?: LscJoinRequest;
  member?: LscMember;
  already_member?: boolean;
  already_approved?: boolean;
  message?: string;
  error?: string;
}

export interface LscJoinRequestAcceptResult {
  success?: boolean;
  status?: string;
  already_member?: boolean;
  joined?: boolean;
  pending_request?: boolean;
  league_id?: string;
  league_name?: string;
  league_slug?: string;
  request?: LscJoinRequest;
  member?: LscMember;
  message?: string;
  error?: string;
}

export interface LscMyJoinRequest {
  id?: string;
  request_id?: string;
  league_id?: string;
  league_name?: string;
  league_slug?: string;
  status?: string;
  display_status?: string;
  is_active_member?: boolean;
  source?: string;
  invite_code?: string;
  requested_role?: string;
  created_at?: string;
  requested_at?: string;
  decided_at?: string | null;
  rejection_reason?: string | null;
  can_open_league?: boolean;
  message?: string;
}

export interface LscMyJoinRequestListResult {
  success?: boolean;
  requests?: LscMyJoinRequest[];
  count?: number;
}

export interface LscVodMatchResult {
  success: boolean;
  dry_run?: boolean;
  candidate_id?: string;
  suggestions?: LscVodMatchSuggestion[];
  best_suggestion?: LscVodMatchSuggestion | null;
  results?: Array<{
    candidate_id?: string;
    suggestions?: LscVodMatchSuggestion[];
    best_suggestion?: LscVodMatchSuggestion | null;
  }>;
  [key: string]: unknown;
}

export type LscLiveLeagueGameVerificationStatus =
  | 'candidate'
  | 'confirmed'
  | 'verified'
  | 'verified_live_game'
  | 'qualified_likely_game'
  | string;

export type LscLiveDashboardTier =
  | 'verified_live_game'
  | 'verified_match_side_pending'
  | 'qualified_likely_game'
  | 'needs_commissioner_review'
  | 'raw_member_stream_detected'
  | 'hidden_not_qualified'
  | string;

export interface LscLiveDashboardRow {
  league_id?: string | null;
  stream_key?: string | null;
  twitch_user_id?: string | null;
  twitch_login?: string | null;
  display_name?: string | null;
  member_id?: string | null;
  member_name?: string | null;
  member_team_name?: string | null;
  member_team_abbreviation?: string | null;
  is_live?: boolean | null;
  stream_started_at?: string | null;
  stream_title?: string | null;
  watch_url?: string | null;
  embed_url?: string | null;
  dashboard_tier?: LscLiveDashboardTier | null;
  dashboard_visible_to_members?: boolean | null;
  dashboard_visible_to_commissioner?: boolean | null;
  live_truth_status?: string | null;
  live_truth_confidence?: number | null;
  visual_status?: string | null;
  observed_team_labels?: string[] | null;
  observed_gamertags?: string[] | null;
  observed_scoreboard?: boolean | null;
  likely_match_id?: string | null;
  likely_opponent_team?: string | null;
  likely_opponent_member?: string | null;
  home_team?: string | null;
  away_team?: string | null;
  stage_label?: string | null;
  season_label?: string | null;
  broadcaster_side?: 'home' | 'away' | string | null;
  schedule_context_status?: string | null;
  needs_commissioner_review?: boolean | null;
  review_reasons?: string[] | null;
  evidence_summary?: string | null;
  recommended_action?: string | null;
  competition_match_id?: string | null;
  matchup_label?: string | null;
  week_label?: string | null;
  schedule_corrected?: boolean | null;
  schedule_corrected_status_label?: string | null;
  matchup_display_mode?: 'verified' | 'neutral' | null;
  matchup_label_suppressed?: boolean | null;
  write_mode?: string | null;
}

export interface LscLiveDashboardResponse {
  league_id: string;
  generated_at?: string | null;
  projection_enabled?: boolean;
  write_mode?: string | null;
  viewer_is_commissioner?: boolean;
  rows: LscLiveDashboardRow[];
  member_visible_rows?: LscLiveDashboardRow[];
  review_queue_rows?: LscLiveDashboardRow[];
  member_visible_count?: number;
  review_queue_count?: number;
  has_active_live_stream?: boolean;
  has_live_verification_in_progress?: boolean;
  suppress_archive_fallback?: boolean;
}

export interface LscLiveLeagueGame {
  live_game_id?: string | null;
  league_id?: string | null;
  match_id: string;
  competition_match_id?: string | null;
  verification_status?: LscLiveLeagueGameVerificationStatus | null;
  match_confidence?: string | null;
  broadcaster_member_id?: string | null;
  broadcaster_display_name?: string | null;
  broadcaster_side?: 'home' | 'away' | string | null;
  broadcaster_twitch_user_id?: string | null;
  twitch_user_id?: string | null;
  twitch_login?: string | null;
  twitch_display_name?: string | null;
  twitch_embed_url?: string | null;
  stream_started_at?: string | null;
  last_event_at?: string | null;
  home_member_id?: string | null;
  away_member_id?: string | null;
  home_team?: string | null;
  away_team?: string | null;
  stage_label?: string | null;
  season_label?: string | null;
  scheduled_at?: string | null;
  verification_confidence?: number | null;
  verified_at?: string | null;
  verification_provider?: string | null;
  evidence_summary?: string | null;
  freshness_expires_at?: string | null;
  review_state?: string | null;
  live_truth_status?: string | null;
  dashboard_tier?: LscLiveDashboardTier | null;
  canonical_state?: string | null;
  review_reasons?: string[] | null;
  recommended_action?: string | null;
  schedule_context_status?: string | null;
  watch_url?: string | null;
  liveStreamKey?: string | null;
  vod_handoff_eligible?: boolean | null;
  embed_url_ready?: boolean;
  schedule_corrected?: boolean | null;
  schedule_corrected_status_label?: string | null;
  matchup_display_mode?: 'verified' | 'neutral' | null;
  matchup_label_suppressed?: boolean | null;
}

export interface LscVodHandoffSession {
  id: string;
  league_id?: string;
  stream_id?: string | null;
  twitch_login?: string | null;
  twitch_user_id?: string | null;
  timeline_label?: string | null;
  home_team?: string | null;
  away_team?: string | null;
  competition_match_id?: string | null;
  stream_started_at?: string | null;
  stream_ended_at?: string | null;
  handoff_status?: string | null;
  handoff_source?: string | null;
  vod_handoff_eligible?: boolean | null;
  vod_handoff_hold_reason?: string | null;
  provider_vod_id?: string | null;
  vod_candidate_id?: string | null;
  handoff_attempt_count?: number | null;
  handoff_link_max_attempts?: number | null;
  live_truth_status?: string | null;
  live_truth_check_id?: string | null;
  live_verified_at?: string | null;
  automation_blocker_code?: string | null;
  automation_blocker_reason?: string | null;
  automation_last_checked_at?: string | null;
  vod_auto_linked_at?: string | null;
  handoff_auto_prepared_at?: string | null;
  updated_at?: string | null;
}

export type LscLiveTruthVerificationStatus =
  | 'verified_live_game'
  | 'verified'
  | 'rejected_menu'
  | 'rejected_non_gameplay'
  | 'needs_schedule_sync'
  | 'verification_failed'
  | 'provider_error'
  | 'verification_pending'
  | string;

export type LscLiveTruthPromotionStatus = 'eligible' | 'blocked' | 'pending' | string;

export interface LscLiveTruthLeagueContext {
  source_state_stale?: boolean;
  source_state_stale_reason?: string | null;
  verified_only_public_mode?: boolean;
  verification_max_age_seconds?: number;
  stream_freshness_seconds?: number;
  min_confidence?: number;
  manual_checks_enabled?: boolean;
  manual_check_cooldown_seconds?: number;
}

export interface LscLiveTruthCandidate {
  competition_match_id?: string | null;
  home_team?: string | null;
  away_team?: string | null;
  season_label?: string | null;
  stage_label?: string | null;
  broadcaster_side?: 'home' | 'away' | string | null;
  match_confidence?: string | null;
  alternate_candidates_count?: number;
}

export interface LscLiveTruthLatestCheck {
  check_id?: string | null;
  status?: LscLiveTruthVerificationStatus | null;
  live_truth_status?: LscLiveTruthVerificationStatus | null;
  verification_status?: LscLiveTruthVerificationStatus | null;
  confidence?: number | null;
  provider?: string | null;
  model?: string | null;
  sample_kind?: string | null;
  sample_index?: number | null;
  sampled_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  check_stale?: boolean;
  evidence_summary?: string | null;
  rejection_reason?: string | null;
  scene_type?: string | null;
  gameplay_detected?: boolean | null;
  game_context_detected?: boolean | null;
  menu_detected?: boolean | null;
  scoreboard_detected?: boolean | null;
  teams_detected?: Record<string, string | null> | null;
  schedule_alignment?: string | null;
  detected_match_summary?: string | null;
  broadcaster_identity_evidence?: string | null;
  commissioner_action?: 'confirm_broadcaster_side' | null;
  review_category?: string | null;
}

export interface LscLiveTruthPromotion {
  eligible?: boolean;
  status?: LscLiveTruthPromotionStatus;
  block_reason?: string | null;
  blocked_reasons?: string[];
  public_block_reason?: string | null;
  public_blocked_reasons?: string[];
  review_state?: string | null;
  would_appear_in_verified_only_live_games?: boolean;
}

export interface LscLiveTruthRetry {
  state?: string | null;
  attempt_count?: number | null;
  max_attempts?: number | null;
  next_retry_at?: string | null;
  last_retry_reason?: string | null;
  stop_reason?: string | null;
}

export interface LscLiveTruthStatusStream {
  twitch_user_id?: string | null;
  twitch_login?: string | null;
  twitch_display_name?: string | null;
  is_live?: boolean;
  stream_id?: string | null;
  stream_started_at?: string | null;
  last_event_at?: string | null;
  stream_state_stale?: boolean;
  live_truth_status_on_stream?: string | null;
  candidate?: LscLiveTruthCandidate | null;
  latest_live_truth?: LscLiveTruthLatestCheck | null;
  promotion?: LscLiveTruthPromotion | null;
  retry?: LscLiveTruthRetry | null;
}

export interface LscLiveTruthStatusResponse {
  league_id: string;
  generated_at?: string | null;
  league_context?: LscLiveTruthLeagueContext;
  streams: LscLiveTruthStatusStream[];
}

export interface RunLscLiveTruthCheckPayload {
  twitch_user_id: string;
  competition_match_id?: string;
  stream_id?: string;
}

export interface RunLscLiveTruthCheckResponse {
  success: boolean;
  league_id: string;
  check?: LscLiveTruthLatestCheck;
  error?: string;
  message?: string;
}

export interface ConfirmLscBroadcasterSidePayload {
  broadcaster_side: 'home' | 'away';
  twitch_user_id?: string;
  stream_id?: string;
  competition_match_id?: string;
  live_truth_check_id?: string;
  needs_attention_item_key?: string;
}

export interface ConfirmLscBroadcasterSideResponse {
  success: boolean;
  league_id: string;
  broadcaster_side: 'home' | 'away';
  idempotent?: boolean;
  live_truth_status?: string;
  side_finalized?: boolean;
  remaining_blockers?: string[];
  confirmation_source?: string;
  check?: LscLiveTruthLatestCheck;
}

export interface LscCommissionerDigestSyncRunSample {
  run_id?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  status?: string | null;
  dry_run?: boolean;
  sync_source?: string | null;
  triggered_by?: string | null;
  guardrail_mode?: string | null;
  members_scanned?: number | null;
  members_skipped?: number | null;
  videos_seen?: number | null;
  candidates_created?: number | null;
  candidates_updated?: number | null;
  warnings_count?: number;
  errors_count?: number;
}

export interface LscAutomationCoverage {
  eligible?: boolean;
  mode?: 'legacy_pilot' | 'paid_beta_entitlement' | 'blocked';
  reason_codes?: string[];
  live_truth_enabled?: boolean;
  vod_handoff_enabled?: boolean;
  vod_handoff_apply_enabled?: boolean;
  dry_run_only?: boolean;
  paid_beta_entitlement_enabled?: boolean;
  legacy_pilot_allowed?: boolean;
}

export interface LscVodSyncCoverage {
  eligible?: boolean;
  mode?: 'legacy_pilot' | 'paid_beta_entitlement' | 'blocked';
  reason_codes?: string[];
  dry_run_only?: boolean;
  legacy_pilot_allowed?: boolean;
  scheduled_enabled?: boolean;
  scheduled_apply_enabled?: boolean;
  canary_apply_enabled?: boolean;
  paid_beta_entitlement_enabled?: boolean;
  internal_enabled?: boolean;
  plan_key?: string | null;
  subscription_status?: string | null;
  read_only?: boolean;
  is_comped?: boolean;
}

export interface LscCommissionerDigestAutomation {
  scheduled_enabled?: boolean;
  scheduled_apply_enabled?: boolean;
  internal_enabled?: boolean;
  scheduler_job_name?: string | null;
  scheduler_cadence?: string | null;
  latest_scheduler_run?: LscCommissionerDigestSyncRunSample | null;
  latest_apply_run?: LscCommissionerDigestSyncRunSample | null;
  latest_manual_run?: LscCommissionerDigestSyncRunSample | null;
  guardrail_summary?: string | null;
  /** @deprecated Prefer automation_coverage for rollout eligibility */
  pilot_league_allowed?: boolean;
  automation_coverage?: LscAutomationCoverage;
  vod_sync_coverage?: LscVodSyncCoverage;
}

export interface LscCommissionerDigestCounts {
  new_synced: number;
  synced_unmatched: number;
  pending_review: number;
  needs_identity_attestation: number;
  ready_to_publish: number;
  recently_published: number;
  member_twitch_issues: number;
  unavailable_count?: number;
}

export interface LscCommissionerDigestCandidateSample {
  candidate_id?: string | null;
  provider_vod_id?: string | null;
  title?: string | null;
  broadcaster_login?: string | null;
  member_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  sync_source?: string | null;
  sync_run_id?: string | null;
  match_status?: string | null;
  review_status?: string | null;
  archive_status?: string | null;
  twitch_url?: string | null;
}

export interface LscCommissionerDigestArchiveSample {
  archive_id?: string | null;
  candidate_id?: string | null;
  title?: string | null;
  broadcaster_login?: string | null;
  published_at?: string | null;
  visibility?: string | null;
  twitch_url?: string | null;
  embed_url?: string | null;
}

export type LscCommissionerDigestSampleCategory =
  | 'new_synced'
  | 'synced_unmatched'
  | 'pending_review'
  | 'needs_identity_attestation'
  | 'ready_to_publish';

export interface LscCommissionerDigestSamples {
  new_synced?: LscCommissionerDigestCandidateSample[];
  synced_unmatched?: LscCommissionerDigestCandidateSample[];
  pending_review?: LscCommissionerDigestCandidateSample[];
  needs_identity_attestation?: LscCommissionerDigestCandidateSample[];
  ready_to_publish?: LscCommissionerDigestCandidateSample[];
  recently_published?: LscCommissionerDigestArchiveSample[];
}

export interface LscCommissionerDigest {
  success?: boolean;
  league_id: string;
  generated_at?: string | null;
  window_hours?: number;
  sample_limit?: number;
  automation: LscCommissionerDigestAutomation;
  counts: LscCommissionerDigestCounts;
  samples: LscCommissionerDigestSamples;
  member_readiness?: LscVodSyncReadiness;
  disclaimers: string[];
}

export interface LscPaidBetaSupportBilling {
  subscription_status?: string | null;
  plan_key?: string | null;
  plan_display_name?: string | null;
  is_comped?: boolean;
  read_only?: boolean;
  bound?: boolean;
}

export interface LscPaidBetaSupportAutomationLiveTruth {
  mode?: string | null;
  eligible?: boolean;
  dry_run_only?: boolean;
  reason_codes: string[];
}

export interface LscPaidBetaSupportAutomationVodHandoff {
  enabled?: boolean;
  apply_enabled?: boolean;
}

export interface LscPaidBetaSupportAutomationVodSync {
  mode?: string | null;
  eligible?: boolean;
  dry_run_only?: boolean;
  scheduled_apply_enabled?: boolean;
  canary_apply_enabled?: boolean;
  reason_codes: string[];
  guardrail_summary?: string | null;
  latest_scheduler_run?: LscCommissionerDigestSyncRunSample | null;
}

export interface LscPaidBetaSupportAutomation {
  live_truth: LscPaidBetaSupportAutomationLiveTruth;
  vod_handoff: LscPaidBetaSupportAutomationVodHandoff;
  vod_sync: LscPaidBetaSupportAutomationVodSync;
  live_reconciliation: {
    enabled?: boolean;
    dry_run_only?: boolean;
  };
}

export interface LscPaidBetaSupportReadiness {
  vod_sync_eligible_members: number;
  pending_twitch_links: number;
  inactive_members: number;
}

export interface LscPaidBetaSupportCounts {
  pending_review: number;
  synced_unmatched: number;
  ready_to_publish: number;
}

export interface LscPaidBetaSupportScheduler {
  scheduled_vod_sync_dry_run_expected: boolean;
  apply_jobs_expected: boolean;
  league_id?: string;
}

export interface LscPaidBetaSupportStatus {
  league_id: string;
  league_name: string;
  generated_at?: string;
  billing: LscPaidBetaSupportBilling;
  automation: LscPaidBetaSupportAutomation;
  readiness: LscPaidBetaSupportReadiness;
  counts: LscPaidBetaSupportCounts;
  scheduler: LscPaidBetaSupportScheduler;
}

export type LscBillingCadence = 'monthly' | 'annual' | 'comped';

export interface LscBillingEntitlementFeatures {
  archive_enabled?: boolean;
  review_enabled?: boolean;
  vod_sync_enabled?: boolean;
  source_sync_enabled?: boolean;
}

export interface LscBillingEntitlementSummary {
  plan_key?: string;
  billing_cadence?: LscBillingCadence | string;
  subscription_status?: string;
  is_comped?: boolean;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  max_members?: number;
  max_active_sources?: number;
  features?: LscBillingEntitlementFeatures;
  support_tier?: string;
  read_only?: boolean;
}

export interface LscBillingBoundLeague {
  league_id: string;
  name: string;
  slug?: string | null;
}

export type LscBillingLifecycleMode =
  | 'billing_active'
  | 'billing_trialing'
  | 'billing_comped'
  | 'billing_unbound'
  | 'billing_past_due'
  | 'billing_canceled'
  | 'billing_incomplete'
  | 'billing_unpaid'
  | 'billing_checkout_required'
  | 'billing_portal_available'
  | 'billing_portal_unavailable'
  | 'billing_recovery_available'
  | 'billing_enforcement_disabled'
  | 'billing_unknown_review_required'
  | string;

export interface LscBillingLifecycleStatus {
  mode: LscBillingLifecycleMode;
  reasons?: string[];
  customer_message?: string;
  recommended_action?: string;
  blocks_paid_mutations?: boolean;
  recovery_available?: boolean;
  portal_available?: boolean;
  checkout_available?: boolean;
}

export interface LscBillingStatusResponse {
  billing_model_enabled: boolean;
  checkout_enabled: boolean;
  enforcement_enabled: boolean;
  status: 'disabled' | 'available' | 'missing' | 'unbound' | string;
  subscription_state?:
    | 'billing_disabled'
    | 'no_subscription'
    | 'account_active_unbound'
    | 'league_subscription_active'
    | 'league_subscription_comped'
    | 'past_due'
    | 'canceled'
    | 'suspended';
  billing_lifecycle?: LscBillingLifecycleStatus | null;
  account_subscription_active?: boolean;
  league_entitlement_bound?: boolean | null;
  entitlement?: LscBillingEntitlementSummary | null;
  bound_leagues?: LscBillingBoundLeague[];
  can_checkout?: boolean;
  can_manage_billing?: boolean;
  billing_cadence?: LscBillingCadence | string | null;
  subscription_status?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
}

export interface LscCheckoutSessionResponse {
  success?: boolean;
  checkout_url: string;
  session_id?: string;
}

export interface LscBillingPortalSessionResponse {
  success?: boolean;
  portal_url: string;
}

export interface LscBindLeagueResponse {
  success: boolean;
  status?: string;
  reason?: string;
  source?: string;
  league_id?: string;
}

export interface LscCommissionerAccessResponse {
  success: boolean;
  can_create_league: boolean;
  reason_code: string;
  recommended_action: 'checkout' | 'manage_billing' | 'none' | string;
  cta?: { label: string; href: string };
  billing_state?: string;
  max_leagues?: number;
  bound_league_count?: number;
  owned_league_count?: number;
}

export type LscLeagueReadinessOverallStatus = 'ready' | 'blocked' | 'warning' | 'preview_only';
export type LscLeagueSetupStatus = 'complete' | 'incomplete' | 'blocked' | 'warning';
export type LscLeagueReadinessItemStatus = 'complete' | 'warning' | 'blocked' | 'not_applicable';
export type LscLeagueReadinessItemSeverity = 'blocking' | 'warning' | 'info';

export interface LscLeagueReadinessScore {
  completed: number;
  total: number;
  blocking: number;
  warnings: number;
  actionable: number;
}

export interface LscLeagueReadinessItem {
  key: string;
  title: string;
  status: LscLeagueReadinessItemStatus;
  severity: LscLeagueReadinessItemSeverity;
  description: string;
  action_label: string;
  action_href: string;
  source?: string;
  can_self_serve?: boolean;
  admin_only?: boolean;
  is_actionable?: boolean;
  is_required_for_completion?: boolean;
  metrics?: Record<string, unknown>;
}

export interface LscLeagueReadinessNextAction {
  key?: string;
  title?: string;
  description?: string;
  action_label?: string;
  action_href?: string;
  severity?: 'blocked' | 'warning';
}

export interface LscLeagueReadinessAutomationSurface {
  eligible: boolean;
  reason_codes?: string[];
  dry_run_only?: boolean;
  canary_enabled?: boolean;
  policy_mode?: string;
}

export interface LscLeagueReadiness {
  league_id: string;
  generated_at: string;
  overall_status: LscLeagueReadinessOverallStatus;
  setup_status: LscLeagueSetupStatus;
  is_complete: boolean;
  should_show_checklist: boolean;
  readiness_score: LscLeagueReadinessScore;
  blocking_items: LscLeagueReadinessItem[];
  warning_items: LscLeagueReadinessItem[];
  completed_items: LscLeagueReadinessItem[];
  actionable_items: LscLeagueReadinessItem[];
  next_best_action: LscLeagueReadinessNextAction | null;
  items: LscLeagueReadinessItem[];
  live_detection_connection?: {
    twitch_linked: number;
    connected: number;
    not_connected_names: string[];
  };
  automation_posture: {
    live_detection: LscLeagueReadinessAutomationSurface;
    public_home_display: LscLeagueReadinessAutomationSurface;
    vod_handoff: LscLeagueReadinessAutomationSurface;
    archive_autopublish: LscLeagueReadinessAutomationSurface;
  };
}

export type LscLeagueSetupProgressStepId =
  | 'subscription'
  | 'league_basics'
  | 'schedule_source'
  | 'members_teams'
  | 'twitch_live'
  | 'automation_settings'
  | 'automation_activation';

export interface LscLeagueSetupProgressStepSummary {
  step_id: LscLeagueSetupProgressStepId;
  title: string;
  status: string;
  is_complete: boolean;
  is_locked: boolean;
}

export interface LscLeagueSetupProgress {
  league_id: string;
  onboarding_version: number;
  current_step_id: LscLeagueSetupProgressStepId;
  resume_step_id: LscLeagueSetupProgressStepId;
  started_at?: string | null;
  last_visited_at?: string | null;
  completed_at?: string | null;
  is_setup_complete: boolean;
  step_ids: LscLeagueSetupProgressStepId[];
  steps: LscLeagueSetupProgressStepSummary[];
  setup_status?: LscLeagueSetupStatus;
  readiness_overall_status?: LscLeagueReadinessOverallStatus;
  readiness?: LscLeagueReadiness;
}

export type LscAutomationAccessOverallStatus =
  | 'enabled'
  | 'partially_enabled'
  | 'preview_only'
  | 'blocked';

export type LscAutomationAccessLaneMode =
  | 'enabled'
  | 'preview'
  | 'dry_run'
  | 'disabled'
  | 'blocked';

export interface LscAutomationAccessLane {
  enabled: boolean;
  mode: LscAutomationAccessLaneMode;
  ready: boolean;
  label: string;
  description: string;
  source: string;
  reason_codes: string[];
  blocking_reason_codes: string[];
  warning_reason_codes: string[];
  requires_admin_enablement: boolean;
  requires_commissioner_action: boolean;
  action_label: string | null;
  action_href: string | null;
}

export interface LscAutomationEnrollmentLane {
  intent_mode: 'disabled' | 'preview' | 'dry_run' | 'enabled' | string;
  intent_enabled: boolean;
  effective_mode: LscAutomationAccessLaneMode | string;
  effective_enabled: boolean;
  effective_matches_intent: boolean;
  requires_env_ceiling?: boolean;
  requires_policy_gate?: boolean;
  requires_billing_gate?: boolean;
  requires_readiness_gate?: boolean;
  notes?: string | null;
}

export interface LscLeagueAutomationEnrollment {
  source: 'firestore' | 'legacy_pilot_fallback' | 'default_absent' | string;
  status: 'disabled' | 'preview' | 'pilot' | 'enabled' | string;
  has_document: boolean;
  read_only: boolean;
  enforcement_active: boolean;
  lanes: Record<string, LscAutomationEnrollmentLane>;
}

export interface LscLeagueAutomationAccessReadiness {
  overall_status: LscLeagueReadinessOverallStatus | string;
  is_complete: boolean;
  should_show_checklist: boolean;
}

export interface LscLeagueAutomationAccess {
  league_id: string;
  generated_at: string;
  overall_status: LscAutomationAccessOverallStatus;
  next_best_action: null;
  readiness: LscLeagueAutomationAccessReadiness;
  lanes: Record<string, LscAutomationAccessLane>;
  enrollment?: LscLeagueAutomationEnrollment;
}

export type LscAutomationActivationStatus =
  | 'setup_incomplete'
  | 'ready_to_activate'
  | 'activation_requested'
  | 'active'
  | 'pending_operator_enablement'
  | 'entitlement_bridge_disabled'
  | 'blocked'
  | 'locked_unpaid';

export type LscAutomationActivationChecklistStatus =
  | 'ready'
  | 'warning'
  | 'blocked'
  | 'pending_operator_enablement'
  | 'entitlement_bridge_disabled'
  | 'locked_unpaid'
  | 'not_applicable';

export interface LscAutomationActivationChecklistItem {
  key: string;
  title: string;
  status: LscAutomationActivationChecklistStatus;
  description: string;
}

export interface LscAutomationActivationLane {
  lane_key: string;
  label: string;
  status: LscAutomationActivationChecklistStatus;
  description: string;
  included_in_bundle: boolean;
}

export type LscDetectionSwitchValue = 'unset' | 'on' | 'off';

export interface LscDetectionState {
  effective: boolean;
  reason: string;
  stored: LscDetectionSwitchValue;
  entitlement_allows: boolean;
}

export interface LscDetectionPayload {
  league_id: string;
  detection: LscDetectionState;
  schedule_stale: boolean;
}

export interface LscAutomationActivationPayload {
  league_id: string;
  generated_at: string;
  activation_status: LscAutomationActivationStatus;
  can_activate: boolean;
  autopublish_enabled: boolean;
  checklist: LscAutomationActivationChecklistItem[];
  bundle_lanes: LscAutomationActivationLane[];
  blockers: string[];
  enrollment_saved: boolean;
  command_center_href: string;
}

// --- LSC support issues (Admin-A/B triage artifact) ---

export const LSC_SUPPORT_ISSUE_CATEGORIES = [
  'billing',
  'setup',
  'schedule',
  'live_truth',
  'vod_handoff',
  'archive_review',
  'login_access',
  'performance',
  'mobile_pwa',
  'reel_failed',
  'lost_credit',
  'quality',
  'other',
] as const;

export type LscSupportIssueCategory = (typeof LSC_SUPPORT_ISSUE_CATEGORIES)[number];

export const RTTS_SUPPORT_ISSUE_CATEGORIES = [
  'reel_failed',
  'lost_credit',
  'quality',
  'billing',
  'other',
] as const;

export const LSC_SUPPORT_ISSUE_SEVERITIES = [
  'low',
  'medium',
  'high',
  'launch_blocker',
] as const;

export type LscSupportIssueSeverity = (typeof LSC_SUPPORT_ISSUE_SEVERITIES)[number];

export const LSC_SUPPORT_ISSUE_STATUSES = [
  'open',
  'triaged',
  'investigating',
  'waiting_on_customer',
  'resolved',
  'dismissed',
] as const;

export type LscSupportIssueStatus = (typeof LSC_SUPPORT_ISSUE_STATUSES)[number];

export const LSC_SUPPORT_ISSUE_STRUCTURED_ERROR_CODES = [
  'admin_forbidden',
  'admin_read_only',
  'issue_not_found',
  'issue_forbidden',
  'issue_invalid_category',
  'issue_invalid_status',
  'issue_invalid_severity',
  'issue_rate_limited',
  'issue_attachment_not_supported',
  'issue_context_too_large',
] as const;

export type LscSupportIssueStructuredErrorCode =
  (typeof LSC_SUPPORT_ISSUE_STRUCTURED_ERROR_CODES)[number];

export interface LscSupportIssueContext {
  route?: string | null;
  frontend_bundle?: string | null;
  backend_health_sha?: string | null;
  league_active_season?: string | null;
  league_active_week?: string | null;
  league_phase?: string | null;
  billing_posture?: {
    billing_model_enabled?: boolean;
    billing_checkout_enabled?: boolean;
    billing_enforcement_enabled?: boolean;
  } | null;
  correlation_id?: string | null;
  error_code?: string | null;
  surface?: string | null;
  browser_family?: string | null;
  timestamp?: string | null;
  job_id?: string | null;
  tier?: string | null;
  reel_state?: string | null;
  outcome?: string | null;
  funding?: string | null;
}

export interface LscSupportIssueAttachmentMetadata {
  filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
}

export interface LscSupportIssue {
  issue_id: string;
  league_id?: string | null;
  reporter_role?: string | null;
  surface?: string | null;
  category: LscSupportIssueCategory;
  severity: LscSupportIssueSeverity;
  title: string;
  description?: string | null;
  steps_to_reproduce?: string | null;
  expected_behavior?: string | null;
  actual_behavior?: string | null;
  status: LscSupportIssueStatus;
  created_at?: string | null;
  updated_at?: string | null;
  public_notes?: string | null;
  context?: LscSupportIssueContext | null;
  attachments?: LscSupportIssueAttachmentMetadata[];
  unread?: boolean;
  last_read_at?: string | null;
  last_reopened_at?: string | null;
  messages?: LscSupportIssueMessage[];
}

export interface LscSupportIssueMessage {
  id: string;
  author_role: 'user' | 'admin' | string;
  author_uid?: string | null;
  body: string;
  created_at?: string | null;
}

export interface LscSupportIssueCreateRequest {
  league_id?: string | null;
  surface?: string | null;
  category: LscSupportIssueCategory;
  severity: LscSupportIssueSeverity;
  title: string;
  description?: string | null;
  steps_to_reproduce?: string | null;
  expected_behavior?: string | null;
  actual_behavior?: string | null;
  context?: LscSupportIssueContext | null;
  attachments?: LscSupportIssueAttachmentMetadata[];
}

export interface LscSupportIssueCreateResponse {
  success: boolean;
  issue: LscSupportIssue;
}

export interface LscSupportIssueMineResponse {
  success: boolean;
  issues: LscSupportIssue[];
  count: number;
}

export interface LscAdminSupportIssue extends LscSupportIssue {
  reporter_uid?: string | null;
  assigned_to?: string | null;
  admin_notes?: string | null;
  deleted?: boolean;
  deleted_at?: string | null;
  remediation?: {
    action?: string | null;
    amount?: number | null;
    status?: string | null;
    created_at?: string | null;
    reel_credit_balance?: number | null;
    reels_remaining?: number | null;
  } | null;
}

export interface LscAdminIssueListFilters {
  status?: LscSupportIssueStatus | '';
  severity?: LscSupportIssueSeverity | '';
  category?: LscSupportIssueCategory | '';
  league_id?: string;
}

export interface LscAdminIssueListResponse {
  success: boolean;
  issues: LscAdminSupportIssue[];
  count: number;
}

export interface LscAdminIssueDetailResponse {
  success: boolean;
  issue: LscAdminSupportIssue;
}

export interface LscAdminIssueUpdateRequest {
  status?: LscSupportIssueStatus;
  assigned_to?: string | null;
  admin_notes?: string | null;
  public_notes?: string | null;
}

export interface LscAdminIssueUpdateResponse {
  success: boolean;
  issue: LscAdminSupportIssue;
}

export interface LscAdminLeagueSummaryIdentity {
  league_id: string;
  league_name: string;
  slug?: string | null;
  game_key?: string | null;
  platform?: string | null;
  owner_uid_compact?: string | null;
  created_at?: string | null;
  status?: string | null;
}

export interface LscAdminLeagueSummaryBilling {
  subscription_status?: string | null;
  plan_key?: string | null;
  plan_display_name?: string | null;
  billing_cadence?: string | null;
  billing_state?: string | null;
  is_comped?: boolean;
  read_only?: boolean;
  bound?: boolean;
}

export interface LscAdminLeagueSummaryReadinessItem {
  key?: string;
  title?: string;
  status?: string;
  severity?: string;
  description?: string;
}

export interface LscAdminLeagueSummaryReadiness {
  overall_status?: string | null;
  setup_status?: string | null;
  is_complete?: boolean;
  should_show_checklist?: boolean;
  blocking_items?: LscAdminLeagueSummaryReadinessItem[];
  warning_items?: LscAdminLeagueSummaryReadinessItem[];
  next_best_action?: Record<string, unknown> | null;
  automation_posture?: Record<string, unknown>;
}

export interface LscAdminLeagueSummarySetupNavigation {
  navigation_only: boolean;
  setup_status?: string | null;
  current_step_id?: string | null;
  resume_step_id?: string | null;
  readiness_overall_status?: string | null;
  steps?: Array<Record<string, unknown>>;
  activation_locked?: boolean;
}

export interface LscAdminLeagueSummaryAutomationLane {
  status?: string | null;
  scope_label?: string | null;
  effective_state?: string | null;
  reason_codes?: string[];
}

export interface LscAdminLeagueSummaryAutomation {
  overall_status?: string | null;
  next_best_action?: Record<string, unknown> | null;
  enrollment?: Record<string, unknown>;
  readiness?: Record<string, unknown>;
  lanes?: Record<string, LscAdminLeagueSummaryAutomationLane>;
}

export interface LscAdminLeagueSummarySchedule {
  active_season_label?: string | null;
  active_stage_label?: string | null;
  season_phase?: string | null;
  schedule_source_connected?: boolean;
  source_state_stale?: boolean;
  is_stale?: boolean;
  repair_needed?: boolean;
  recommended_action?: string | null;
  stale_signals?: string[];
  blockers?: string[];
  current_context?: Record<string, unknown>;
  latest_repair_run?: Record<string, unknown> | null;
  schedule_source?: Record<string, unknown> | null;
}

export interface LscAdminLeagueSummaryArchiveAutopublish {
  autopublish_enabled?: boolean;
  autopublish_mode?: string | null;
  controlled_apply_actionable?: boolean;
  publish_behavior_enabled?: boolean;
  customer_message?: string | null;
  stage_gated?: boolean;
}

export interface LscAdminLeagueSummaryWorkflows {
  needs_attention_count?: number;
  side_confirmation_blockers_count?: number | null;
  vod_handoff_blockers_count?: number;
  archive_review_count?: number;
  archive_autopublish?: LscAdminLeagueSummaryArchiveAutopublish;
}

export interface LscAdminLeagueSummaryIssueRow {
  issue_id?: string;
  title?: string;
  category?: string;
  severity?: string;
  status?: string;
  surface?: string;
  updated_at?: string;
  created_at?: string;
}

export interface LscAdminLeagueSummaryIssues {
  open_issue_count?: number;
  launch_blocker_issue_count?: number;
  total_issue_count?: number;
  latest_issues?: LscAdminLeagueSummaryIssueRow[];
}

export interface LscAdminLeagueSummary {
  league_id: string;
  generated_at?: string;
  read_only?: boolean;
  identity: LscAdminLeagueSummaryIdentity;
  billing: LscAdminLeagueSummaryBilling;
  readiness: LscAdminLeagueSummaryReadiness;
  setup_navigation: LscAdminLeagueSummarySetupNavigation;
  automation: LscAdminLeagueSummaryAutomation;
  schedule: LscAdminLeagueSummarySchedule;
  workflows: LscAdminLeagueSummaryWorkflows;
  issues: LscAdminLeagueSummaryIssues;
}

export interface LscAdminLeagueSummaryResponse {
  success: boolean;
  summary: LscAdminLeagueSummary;
}

export interface LscAdminHealthBackend {
  git_sha?: string | null;
  revision?: string | null;
  service?: string | null;
  status?: string | null;
}

export interface LscAdminHealthBilling {
  billing_model_enabled?: boolean;
  checkout_enabled?: boolean;
  enforcement_enabled?: boolean;
}

export interface LscAdminHealthBridge {
  paid_beta_automation_entitlement_enabled?: boolean;
}

export interface LscAdminHealthSmoke {
  baseline_accepted?: string | null;
  last_verified_at?: string | null;
  note?: string | null;
}

export interface LscAdminHealthKnownWarning {
  id?: string;
  severity?: string;
  smoke_check?: string;
  summary?: string;
  customer_visible?: boolean;
  blocks_admin_deploy?: boolean;
}

export interface LscAdminHealthRelease {
  support_issue_backend?: boolean;
  report_issue_ux?: boolean;
  admin_issue_queue?: boolean;
  league_health_summary?: boolean;
  production_health_widget?: boolean;
}

export interface LscAdminHealthLaunchReadiness {
  expanded_readiness_marker?: string | null;
  original_readiness_marker?: string | null;
  source?: string | null;
  dynamic_calculation?: boolean;
}

export interface LscAdminHealthSafetyLocks {
  env_mutation_unavailable?: boolean;
  scheduler_controls_unavailable?: boolean;
  cloud_tasks_controls_unavailable?: boolean;
  stripe_mutation_unavailable?: boolean;
  firestore_editor_unavailable?: boolean;
  archive_autopublish_stage2_locked?: boolean;
  controlled_apply_locked?: boolean;
}

export interface LscAdminHealth {
  generated_at?: string;
  read_only?: boolean;
  backend?: LscAdminHealthBackend;
  billing?: LscAdminHealthBilling;
  bridge?: LscAdminHealthBridge;
  smoke?: LscAdminHealthSmoke;
  known_warnings?: LscAdminHealthKnownWarning[];
  admin_release?: LscAdminHealthRelease;
  launch_readiness?: LscAdminHealthLaunchReadiness;
  deferred_verification?: string[];
  safety_locks?: LscAdminHealthSafetyLocks;
}

export interface LscAdminHealthResponse extends LscAdminHealth {
  success: boolean;
}
